'use client';

import { useState, type FormEvent } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Mail, Phone, Send } from 'lucide-react';
import Reveal from './Reveal';

export default function Contact() {
  const { t } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const form = e.currentTarget;
    const data = {
      name: (form.elements.namedItem('name') as HTMLInputElement).value,
      organization: (form.elements.namedItem('organization') as HTMLInputElement).value,
      email: (form.elements.namedItem('email') as HTMLInputElement).value,
      phone: (form.elements.namedItem('phone') as HTMLInputElement).value,
      message: (form.elements.namedItem('message') as HTMLTextAreaElement).value,
    };

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) throw new Error();
      setSubmitted(true);
    } catch {
      setError(t(
        "Une erreur est survenue. Veuillez réessayer.",
        "Something went wrong. Please try again."
      ));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-dark-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <Reveal>
            <div>
              <p className="text-sm font-semibold text-electric tracking-widest uppercase mb-3">
                Contact
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t('Parlons de votre IT', "Let\u2019s talk about your IT")}
              </h2>
              <p className="text-white/55 text-lg leading-relaxed mb-10">
                {t(
                  'Remplissez le formulaire ci-dessous et nous vous recontacterons sous 24h pour planifier un premier échange.',
                  "Fill out the form below and we\u2019ll get back to you within 24 hours to schedule an initial conversation."
                )}
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-white/50">
                  <div className="w-10 h-10 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
                    <Mail className="w-4 h-4 text-electric/60" />
                  </div>
                  <span>contact@quorova.com</span>
                </div>
                <div className="flex items-center gap-3 text-white/50">
                  <div className="w-10 h-10 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
                    <Phone className="w-4 h-4 text-electric/60" />
                  </div>
                  <a href="tel:+33763406885" className="hover:text-white/80 transition-colors">
                    <span className="text-white/30 mr-1.5">FR</span>+33 7 63 40 68 85
                  </a>
                </div>
                <div className="flex items-center gap-3 text-white/50">
                  <div className="w-10 h-10 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
                    <Phone className="w-4 h-4 text-electric/60" />
                  </div>
                  <a href="tel:+32491344499" className="hover:text-white/80 transition-colors">
                    <span className="text-white/30 mr-1.5">BE</span>+32 4 91 34 44 99
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div>
              {submitted ? (
                <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm p-8 md:p-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-electric/10 border border-electric/20 flex items-center justify-center mx-auto mb-4">
                    <Send className="w-7 h-7 text-electric" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {t('Message envoyé', 'Message sent')}
                  </h3>
                  <p className="text-white/50">
                    {t(
                      'Merci pour votre message. Nous vous recontacterons sous 24h.',
                      "Thank you for your message. We\u2019ll get back to you within 24 hours."
                    )}
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="rounded-2xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm p-8 md:p-10 space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-white/60 mb-1.5"
                      >
                        {t('Nom', 'Name')} *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-electric/30 focus:border-electric/30 transition-all"
                        placeholder={t('Votre nom', 'Your name')}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="organization"
                        className="block text-sm font-medium text-white/60 mb-1.5"
                      >
                        {t('Organisation', 'Organization')}
                      </label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        className="w-full px-4 py-2.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-electric/30 focus:border-electric/30 transition-all"
                        placeholder={t('Votre organisation', 'Your organization')}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-white/60 mb-1.5"
                      >
                        {t('Email *', 'Email *')}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-electric/30 focus:border-electric/30 transition-all"
                        placeholder="email@example.com"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-white/60 mb-1.5"
                      >
                        {t('Téléphone', 'Phone')}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-4 py-2.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-electric/30 focus:border-electric/30 transition-all"
                        placeholder={t('+33 7 63 48 67 89', '+1 (555) 000-0000')}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-white/60 mb-1.5"
                    >
                      {t('Message *', 'Message *')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-electric/30 focus:border-electric/30 transition-all resize-none"
                      placeholder={t(
                        'Décrivez brièvement votre besoin...',
                        'Briefly describe your needs...'
                      )}
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-400 bg-red-400/10 px-4 py-2.5 rounded-lg border border-red-400/20">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 py-3 text-base font-semibold text-white bg-electric rounded-xl hover:bg-electric-bright transition-all duration-300 shadow-[0_0_20px_rgba(79,142,255,0.2)] hover:shadow-[0_0_30px_rgba(79,142,255,0.35)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading
                      ? t('Envoi en cours...', 'Sending...')
                      : t('Envoyer', 'Send')}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
